import { Box, Button, Flex } from '@tempmee/react';
import { useFormContext } from 'react-hook-form';
import { useGetProfessionalCountsByLocation } from '~/domain/Professionals/usecases/useGetProfessionalCountsByLocation';
import { useUpdateOfficeSetup } from '~/domain/UpdateOfficeSetup';
import { useUserSelector } from '~/hooks/useUserSelector';
import { fireAdditionalOfficeInfoSubmitted } from '~/posthogs/events/Signup';
import { AuthErrorBanner, useAuthFormSubmit } from '../../shared';
import type { ISignUpFormValues } from '../types';
import { buildUpdateOfficeSetupInput } from './buildUpdateOfficeSetupInput';
import { PROFESSIONAL_RADIUS_MILES } from './constants';
import {
  AssistedHygieneSection,
  InsuranceSection,
  OfficeSoftwareSection,
  ParkingSection,
  TimePerPatientSection,
} from './sections';
import { useOfficeSetupHydration } from './useOfficeSetupHydration';

interface IUserLike {
  id?: string;
  address?: Array<{ latitude?: string | null; longitude?: string | null }> | null;
}

const FORM_WIDTH = 704;
const CTA_WIDTH = 296;

export interface IOfficeSetupFormProps {
  /** When true, the submit flags the user as is_profile_complete. Callers
   *  gate this on whether the user's state requires the worker-type step. */
  isProfileComplete: boolean;
  onSuccess: () => void;
}

export const OfficeSetupForm = ({ isProfileComplete, onSuccess }: IOfficeSetupFormProps) => {
  const { mutateAsync: updateOfficeSetup, isPending } = useUpdateOfficeSetup();
  useOfficeSetupHydration();

  const user = useUserSelector() as IUserLike | null | undefined;
  const lat = parseFloat(user?.address?.[0]?.latitude ?? '');
  const lng = parseFloat(user?.address?.[0]?.longitude ?? '');
  // Re-uses the same query as the Step 4 header copy (`useOfficeSetupCopy`),
  // so React Query serves it from cache — no extra network hop.
  const { totalCount, isLoading, isError } = useGetProfessionalCountsByLocation(lat, lng, {
    distance: PROFESSIONAL_RADIUS_MILES,
  });

  const {
    getValues,
    reset,
    formState: { dirtyFields },
  } = useFormContext<ISignUpFormValues>();

  const { submit, bannerVisible, bannerRef, serverMessage } = useAuthFormSubmit<ISignUpFormValues>({
    onValid: async (values) => {
      const professionalCountReady = !isLoading && !isError;
      // professionalCount omitted when the count query hasn't resolved —
      // keeps the funnel from being skewed by a default-0 sent during
      // loading/error.
      fireAdditionalOfficeInfoSubmitted(
        professionalCountReady ? { professionalCount: totalCount } : {},
      );
      // dirtyFields tracks manual edits only (isDirty also flips on hydration
      // via setValue, so it's unreliable for a "did the user touch this?"
      // gate when the form is populated from BE).
      const userEdited = Object.keys(dirtyFields).length > 0;
      if (!userEdited) {
        onSuccess();
        return;
      }
      await updateOfficeSetup(buildUpdateOfficeSetupInput({ values, isProfileComplete }));
      // Snapshot saved values as the new clean baseline so a resubmit
      // without changes short-circuits.
      reset(getValues());
      onSuccess();
    },
  });

  return (
    <Box style={{ width: FORM_WIDTH }}>
      <form onSubmit={submit} data-testid="office-setup-form" noValidate>
        <Box
          ref={bannerRef}
          style={{ marginTop: bannerVisible ? -16 : 0, marginBottom: bannerVisible ? 8 : 0 }}
        >
          <AuthErrorBanner visible={bannerVisible} message={serverMessage} />
        </Box>

        <TimePerPatientSection />
        <AssistedHygieneSection />
        <ParkingSection />
        <OfficeSoftwareSection />
        <InsuranceSection />

        <Flex justify="center" style={{ marginTop: 48 }}>
          <Box style={{ width: CTA_WIDTH }}>
            <Button
              data-cy="btn-office-setup-continue"
              type="submit"
              variant="primary"
              size="lg"
              isLoading={isPending}
              style={{ width: '100%' }}
            >
              Start Requesting Coverage
            </Button>
          </Box>
        </Flex>
      </form>
    </Box>
  );
};
